const Wishlist = {
  key: 'knakrd_wishlist',

  get() {
    try {
      return JSON.parse(localStorage.getItem(this.key)) || [];
    } catch (e) {
      return [];
    }
  },

  set(handles) {
    localStorage.setItem(this.key, JSON.stringify(handles));
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles } }));
  },

  has(handle) {
    return this.get().includes(handle);
  },

  toggle(handle) {
    const handles = this.get();
    const index = handles.indexOf(handle);
    if (index > -1) {
      handles.splice(index, 1);
    } else {
      handles.push(handle);
    }
    this.set(handles);
    return handles.includes(handle);
  },

  count() {
    return this.get().length;
  },
};

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.productHandle;
    this.button = this.querySelector('button');
    this.updateState();

    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      const saved = Wishlist.toggle(this.handle);
      this.updateState(saved);
    });

    this.changeListener = () => this.updateState();
    document.addEventListener('wishlist:change', this.changeListener);
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:change', this.changeListener);
  }

  updateState() {
    const saved = Wishlist.has(this.handle);
    this.classList.toggle('wishlist-button--saved', saved);
    this.button.setAttribute('aria-pressed', saved);
    this.button.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Add to wishlist');
  }
}

class WishlistIcon extends HTMLElement {
  connectedCallback() {
    this.bubble = this.querySelector('.wishlist-count-bubble');
    this.countEl = this.querySelector('[data-wishlist-count]');
    this.updateCount();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const drawer = document.querySelector('wishlist-drawer');
      drawer?.open();
    });

    document.addEventListener('wishlist:change', () => this.updateCount());
  }

  updateCount() {
    const count = Wishlist.count();
    if (this.countEl) this.countEl.textContent = count;
    if (this.bubble) this.bubble.hidden = count === 0;
  }
}

class WishlistDrawer extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector('.wishlist-drawer__overlay');
    this.closeBtn = this.querySelector('.wishlist-drawer__close');
    this.content = this.querySelector('.wishlist-drawer__content');

    this.overlay?.addEventListener('click', () => this.close());
    this.closeBtn?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }

  open() {
    this.classList.add('wishlist-drawer--open');
    document.body.classList.add('overflow-hidden');
    this.render();
  }

  close() {
    this.classList.remove('wishlist-drawer--open');
    document.body.classList.remove('overflow-hidden');
  }

  async render() {
    const handles = Wishlist.get();
    if (handles.length === 0) {
      this.content.innerHTML = '<p class="wishlist-drawer__empty">Nothing saved yet — tap the heart on any product to add it here.</p>';
      return;
    }

    this.content.innerHTML = '<p class="wishlist-drawer__loading">Loading...</p>';

    const products = await Promise.all(
      handles.map((handle) =>
        fetch(`/products/${handle}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );

    const validProducts = products.filter(Boolean);

    if (validProducts.length === 0) {
      this.content.innerHTML = '<p class="wishlist-drawer__empty">Nothing saved yet — tap the heart on any product to add it here.</p>';
      return;
    }

    this.content.innerHTML = `<ul class="wishlist-drawer__list">${validProducts
      .map(
        (product) => `
          <li class="wishlist-drawer__item">
            <a href="${product.url}" class="wishlist-drawer__item-link">
              <img src="${product.featured_image || ''}" alt="${product.title}" width="80" height="80" loading="lazy">
              <span class="wishlist-drawer__item-info">
                <span class="wishlist-drawer__item-title">${product.title}</span>
                <span class="wishlist-drawer__item-price">${(product.price / 100).toFixed(2)}</span>
              </span>
            </a>
            <button type="button" class="wishlist-drawer__item-remove" data-remove-handle="${product.handle}" aria-label="Remove ${product.title} from wishlist">&times;</button>
          </li>`
      )
      .join('')}</ul>`;

    this.content.querySelectorAll('[data-remove-handle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        Wishlist.toggle(btn.dataset.removeHandle);
        this.render();
      });
    });
  }
}

customElements.define('wishlist-button', WishlistButton);
customElements.define('wishlist-icon', WishlistIcon);
customElements.define('wishlist-drawer', WishlistDrawer);
